import { useState } from "react";
import axios from "axios";
import { ArrowUpRight, Loader2, Check } from "lucide-react";
import { track } from "@/lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ArticleLeadCapture({ articleSlug, articleTitle }) {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      setError("Please enter your email.");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      await axios.post(`${API}/newsletter`, {
        email: email.trim(),
        source: `insight:${articleSlug}`,
      });
      track("newsletter_signup", { source: "insight-article", article: articleSlug });
      setDone(true);
    } catch (err) {
      setError(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="article-lead-capture"
      className="mt-10 border-2 border-black bg-white p-6 md:p-8"
    >
      <div className="overline">Enjoyed This Perspective?</div>
      <p className="mt-3 font-serif-display text-xl md:text-2xl text-black leading-snug max-w-xl">
        Get the next one in your inbox.
      </p>
      <p className="mt-2 text-sm text-zinc-600 leading-relaxed max-w-xl">
        Considered pieces on growth, pricing, channels and demand — written for
        leaders, sent occasionally. No noise.
      </p>
      {done ? (
        <p
          data-testid="alc-success"
          className="mt-5 inline-flex items-center gap-2 text-sm font-medium text-black"
        >
          <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-black text-white">
            <Check className="h-3.5 w-3.5" />
          </span>
          You're on the list. The next piece will find you.
        </p>
      ) : (
        <form onSubmit={submit} className="mt-5 flex flex-col sm:flex-row gap-3 max-w-xl">
          <input
            data-testid="alc-email"
            type="email"
            required
            aria-label={`Email address — subscribe after reading ${articleTitle}`}
            placeholder="you@company.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="flex-1 h-12 px-4 bg-transparent border border-black/30 text-black placeholder:text-zinc-400 focus:outline-none focus:border-black text-sm"
          />
          <button
            data-testid="alc-submit"
            type="submit"
            disabled={submitting}
            className="inline-flex items-center justify-center gap-2 h-12 px-6 bg-black text-white text-sm font-medium hover:bg-zinc-800 transition-colors disabled:opacity-60"
          >
            {submitting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                Subscribe <ArrowUpRight className="h-4 w-4" />
              </>
            )}
          </button>
        </form>
      )}
      {error && (
        <p data-testid="alc-error" className="mt-3 text-sm text-red-700">
          {error}
        </p>
      )}
      {!done && (
        <p className="mt-3 text-xs text-zinc-400">
          No spam. Unsubscribe anytime.
        </p>
      )}
    </div>
  );
}
